import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';
import { Card, PrimaryButton, TicketingShell } from '../../components/ticketing/TicketingShell';
import { ApiService } from '../../utils/api';
import { useEventRoute } from '../../utils/eventRef';
import { formatMoney, useTicketing } from '../../utils/ticketing';
import { openCashfreeCheckout, submitPayuForm } from '../../utils/cashfree';

const COPY = {
  CONFIRMED: { icon: CheckCircle2, tone: 'text-green-600', title: 'Registration confirmed', body: 'A confirmation email with the QR code has been sent to each attendee.' },
  PENDING_APPROVAL: { icon: Clock, tone: 'text-amber-600', title: 'Submitted for approval', body: 'The organiser will review your registration. You\'ll get an email once it is approved.' },
  PENDING_PAYMENT: { icon: Clock, tone: 'text-amber-600', title: 'Payment pending', body: 'We haven\'t received the payment yet. If you have paid, this page updates in a moment.' },
  PAYMENT_FAILED: { icon: XCircle, tone: 'text-red-600', title: 'Payment not completed', body: 'No money was taken, or it will be refunded by your bank. You can try again.' },
  CANCELLED: { icon: XCircle, tone: 'text-red-600', title: 'Booking cancelled', body: 'This booking was cancelled.' },
};

const ATTENDEE_STATUS = {
  REGISTERED: 'Confirmed', PENDING_PAYMENT: 'Awaiting payment', PENDING_FOR_APPROVAL: 'Awaiting approval',
  PAYMENT_FAILED: 'Payment failed', CANCELLED: 'Cancelled', REJECTED: 'Rejected',
};

/** /register/:event/booking/:bookingId — result after booking or returning from the payment page. */
const BookingStatusPage = () => {
  const { eventId, eventRef } = useEventRoute();
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const orderId = params.get('order_id');
  const [polls, setPolls] = useState(0);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const { data: view } = useTicketing(eventRef);
  const { data: booking, isLoading, isError, refetch } = useQuery({
    queryKey: ['booking', eventId, bookingId, orderId],
    queryFn: () => ApiService.getBooking(eventId, bookingId, orderId),
    enabled: !!eventId,
  });

  // Gateways confirm a few seconds after the browser comes back.
  useEffect(() => {
    if (booking?.status !== 'PENDING_PAYMENT' || !orderId || polls >= 12) return undefined;
    const timer = setTimeout(() => { setPolls((p) => p + 1); refetch(); }, 3000);
    return () => clearTimeout(timer);
  }, [booking, orderId, polls, refetch]);

  const pay = async () => {
    setPaying(true);
    setPayError(null);
    try {
      const order = await ApiService.startBookingCheckout(eventId, bookingId);
      if (order.provider === 'PAYU') {
        submitPayuForm(order.payu);
        return;
      }
      await openCashfreeCheckout(order.paymentSessionId, order.cashfreeMode);
      window.location.replace(`/register/${eventRef}/booking/${bookingId}?order_id=${encodeURIComponent(order.orderId)}`);
    } catch (err) {
      setPayError(err.message || 'Payment could not be started');
    } finally {
      setPaying(false);
    }
  };

  const shellView = view?.ticketing ? view : { name: view?.name, ticketing: {} };
  if (isLoading || !eventId) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;
  }
  if (isError || !booking) {
    return (
      <TicketingShell view={shellView} title={view?.name}>
        <Card className="p-8 text-center text-gray-600">We couldn't find this booking.</Card>
      </TicketingShell>
    );
  }
  const copy = COPY[booking.status] || COPY.PENDING_PAYMENT;
  const Icon = copy.icon;
  const waiting = booking.status === 'PENDING_PAYMENT' && orderId && polls < 12;

  return (
    <TicketingShell view={shellView} title={view?.name} subtitle={view?.ticketing?.tagline}>
      <div className="mx-auto max-w-2xl">
        <Card className="p-6 text-center sm:p-10">
          {waiting ? <Loader2 className="mx-auto h-14 w-14 animate-spin text-gray-400" /> : <Icon className={`mx-auto h-14 w-14 ${copy.tone}`} />}
          <h1 className="mt-4 text-2xl font-bold sm:text-3xl">{waiting ? 'Confirming your payment…' : copy.title}</h1>
          <p className="mt-2 text-gray-600">{waiting ? 'This takes a few seconds. Please don\'t close this page.' : copy.body}</p>
          <dl className="mx-auto mt-6 grid max-w-md grid-cols-2 gap-3 rounded-xl bg-gray-50 p-4 text-left text-sm">
            <dt className="text-gray-500">Booking ID</dt><dd className="font-mono font-semibold">{booking.bookingId}</dd>
            <dt className="text-gray-500">Pass</dt><dd className="font-semibold">{booking.passName}</dd>
            {booking.amount > 0 && (<><dt className="text-gray-500">Amount</dt><dd className="font-semibold">{formatMoney(booking.amount, booking.currency)}</dd></>)}
          </dl>
          {(booking.status === 'PENDING_PAYMENT' || booking.status === 'PAYMENT_FAILED') && !waiting && (
            <div className="mt-6">
              <PrimaryButton onClick={pay} disabled={paying} className="min-w-[200px]">
                {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : `Pay ${formatMoney(booking.amount, booking.currency)}`}
              </PrimaryButton>
              {payError && <p className="mt-2 text-sm text-red-600">{payError}</p>}
            </div>
          )}
        </Card>
        <Card className="mt-6">
          <h2 className="border-b px-6 py-4 text-lg font-semibold">Attendees</h2>
          <ul className="divide-y">
            {booking.attendees.map((a) => (
              <li key={a.userId} className="flex items-center justify-between gap-3 px-6 py-4">
                <div className="min-w-0">
                  <p className="truncate font-medium">{a.name || 'Attendee'}</p>
                  <p className="font-mono text-sm text-gray-500">{a.userId}</p>
                </div>
                <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">{ATTENDEE_STATUS[a.status] || a.status}</span>
              </li>
            ))}
          </ul>
        </Card>
        <p className="mt-6 text-center"><Link to={`/register/${eventRef}`} className="text-sm font-medium text-[var(--tk-primary)] underline">Back to event</Link></p>
      </div>
    </TicketingShell>
  );
};

export default BookingStatusPage;
